import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../SEO';
import { Breadcrumbs, Icon, Badge, Button, EmptyState } from '../ui';

const LocationDetails = ({ routeData, locationType, baseUrl, idParamName }) => {
  const { t } = useTranslation();
  const params = useParams();
  const locationId = params[idParamName];

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [gallery, setGallery] = useState([]);
  const [nextLocation, setNextLocation] = useState(null);

  const location = routeData.find((loc) => loc.path === locationId);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!location) return;
    setGallery(location.gallery || [location.image]);
    setCurrentImage(0);
    const idx = routeData.findIndex((loc) => loc.path === locationId);
    setNextLocation(routeData[(idx + 1) % routeData.length]);
  }, [locationId, routeData, location]);

  const prevImg = useCallback(() => setCurrentImage((i) => (i === 0 ? gallery.length - 1 : i - 1)), [gallery.length]);
  const nextImg = useCallback(() => setCurrentImage((i) => (i === gallery.length - 1 ? 0 : i + 1)), [gallery.length]);

  useEffect(() => {
    if (!galleryOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prevImg();
      if (e.key === 'ArrowRight') nextImg();
      if (e.key === 'Escape') setGalleryOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [galleryOpen, prevImg, nextImg]);

  if (!location) {
    return (
      <div className="container detail-wrap">
        <EmptyState
          icon="waves"
          title={t('{{type}} not found', { type: t(locationType) })}
          body={t('This place may have been renamed or removed.')}
          action={<Button variant="outline" to={baseUrl}>{t('Go back')}</Button>}
        />
      </div>
    );
  }

  const [lat, lng] = location.coordinates || [];
  const mapsUrl = `https://maps.google.com/?q=${lat},${lng}`;

  const locationSchema = {
    '@context': 'https://schema.org',
    '@type': 'TouristAttraction',
    name: location.name,
    description: location.description,
    image: location.image,
    url: `https://ayodhyadham.info${baseUrl}/${location.path}`,
    address: { '@type': 'PostalAddress', addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' },
    geo: { '@type': 'GeoCoordinates', latitude: lat, longitude: lng },
    isAccessibleForFree: true,
  };

  return (
    <>
      <SEO
        title={`${location.name} — ${t(locationType)} in Ayodhya`}
        description={location.description}
        canonicalUrl={`${baseUrl}/${location.path}`}
        image={location.image}
        structuredData={locationSchema}
      />

      <div className="container detail-wrap">
        <Breadcrumbs
          className="detail__crumbs"
          items={[{ label: t('Home'), to: '/' }, { label: t(`${locationType}s`), to: baseUrl }, { label: t(location.name) }]}
        />

        <div className="detail">
          <div className="detail__main">
            <div className="gallery">
              <button type="button" className="gallery__main" onClick={() => setGalleryOpen(true)} aria-label={t('Open gallery')} style={{ border: 0, padding: 0 }}>
                <img src={gallery[currentImage] || location.image} alt={location.name} />
                {gallery.length > 1 && <Badge variant="soft" className="gallery__count"><Icon name="camera" size={13} />{currentImage + 1}/{gallery.length}</Badge>}
              </button>
              {gallery.length > 1 && (
                <div className="gallery__thumbs">
                  {gallery.map((src, i) => (
                    <button key={src} type="button" className={`gallery__thumb${i === currentImage ? ' is-active' : ''}`} onClick={() => setCurrentImage(i)} aria-label={t('View image {{n}}', { n: i + 1 })}>
                      <img src={src} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="detail__head">
              {location.tags?.length > 0 && (
                <div className="detail__tags">
                  {location.tags.map((tag) => <Badge key={tag} variant="soft">{t(tag)}</Badge>)}
                </div>
              )}
              <h1 className="detail__title">{t(location.name)}</h1>
              <div className="detail__meta">
                <span><Icon name="clock" size={15} />{t(location.timings)}</span>
                <span><Icon name="map-pin" size={15} />{t('Ayodhya, Uttar Pradesh')}</span>
                <span><Icon name="indian-rupee" size={15} />{t('Free entry')}</span>
              </div>
            </div>

            <section className="detail__section">
              <h2 className="h-section">{t('About')}</h2>
              <div className="prose"><p>{location.description}</p></div>
            </section>

            {location.significance && (
              <section className="detail__section">
                <h2 className="h-section">{t('Significance')}</h2>
                <div className="prose"><p>{location.significance}</p></div>
              </section>
            )}

            <section className="detail__section">
              <h2 className="h-section">{t('Visitor information')}</h2>
              <ul className="info-list">
                <li>
                  <span className="info-list__icon"><Icon name="clock" size={16} /></span>
                  <div><div className="info-list__label">{t('Timings')}</div><div className="info-list__value">{t(location.timings)}</div></div>
                </li>
                <li>
                  <span className="info-list__icon"><Icon name="sunrise" size={16} /></span>
                  <div><div className="info-list__label">{t('Best time to visit')}</div><div className="info-list__value">{t('Sunrise for a quiet dip, sunset for the aarti')}</div></div>
                </li>
                <li>
                  <span className="info-list__icon"><Icon name="navigation" size={16} /></span>
                  <div><div className="info-list__label">{t('How to reach')}</div><div className="info-list__value">{t('E-rickshaw or auto from Hanuman Garhi, 10–20 minutes')}</div></div>
                </li>
                <li>
                  <span className="info-list__icon"><Icon name="info" size={16} /></span>
                  <div><div className="info-list__label">{t('Good to know')}</div><div className="info-list__value">{t('Footwear counters are available. Carry small cash for offerings.')}</div></div>
                </li>
              </ul>
            </section>
          </div>

          <aside className="detail__aside">
            <div className="card aside-card">
              <h3 className="h-card">{t('Plan your visit')}</h3>
              <div className="aside-card__rows">
                <div className="aside-card__row"><span>{t('Entry')}</span><span>{t('Free')}</span></div>
                <div className="aside-card__row"><span>{t('Timings')}</span><span>{t(location.timings)}</span></div>
              </div>
              <Button block href={mapsUrl} icon={<Icon name="navigation" size={16} />} iconPosition="left">{t('Get directions')}</Button>
              <Button block variant="outline" to="/darshan-booking" icon={<Icon name="temple" size={16} />} iconPosition="left">{t('Darshan Booking')}</Button>
            </div>

            {nextLocation && (
              <Link to={`${baseUrl}/${nextLocation.path}`} className="card card--hover next-place">
                <span className="next-place__media"><img src={nextLocation.image} alt="" loading="lazy" /></span>
                <span>
                  <span className="next-place__label">{t('Next')}</span>
                  <span className="next-place__name">{t(nextLocation.name)}</span>
                </span>
              </Link>
            )}
          </aside>
        </div>
      </div>

      {galleryOpen && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={t('Gallery')} onClick={() => setGalleryOpen(false)}>
          <img src={gallery[currentImage]} alt={location.name} onClick={(e) => e.stopPropagation()} />
          <button type="button" className="lightbox__close" aria-label={t('Close')} onClick={() => setGalleryOpen(false)}><Icon name="x" size={20} /></button>
          {gallery.length > 1 && (
            <>
              <button type="button" className="lightbox__prev" aria-label={t('Previous')} onClick={(e) => { e.stopPropagation(); prevImg(); }}><Icon name="chevron-left" size={20} /></button>
              <button type="button" className="lightbox__next" aria-label={t('Next')} onClick={(e) => { e.stopPropagation(); nextImg(); }}><Icon name="chevron-right" size={20} /></button>
              <div className="lightbox__counter">{currentImage + 1} / {gallery.length}</div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default LocationDetails;
